// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/agent/:threadId*?\",\"regex\":\"^/agent(?:/(.*))?$\",\"paramNames\":[\"threadId\"],\"hasOptionalPrefix\":false},{\"path\":\"/portfolio/:address\",\"regex\":\"^/portfolio/([^/]+)$\",\"paramNames\":[\"address\"],\"hasOptionalPrefix\":false},{\"path\":\"/r/:code\",\"regex\":\"^/r/([^/]+)$\",\"paramNames\":[\"code\"],\"hasOptionalPrefix\":false},{\"path\":\"/trade/:market-name*?\",\"regex\":\"^/trade(?:/(.*))?$\",\"paramNames\":[\"market-name\"],\"hasOptionalPrefix\":false},{\"path\":\"/vaults/:address\",\"regex\":\"^/vaults/([^/]+)$\",\"paramNames\":[\"address\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/accounts\"},{\"path\":\"/affiliates\"},{\"path\":\"/affiliates/clients\"},{\"path\":\"/affiliates/commissions\"},{\"path\":\"/affiliates/levels\"},{\"path\":\"/affiliates/overview\"},{\"path\":\"/affiliates/performance\"},{\"path\":\"/affiliates/points\"},{\"path\":\"/affiliates/sub-affiliates\"},{\"path\":\"/announcements\"},{\"path\":\"/api\"},{\"path\":\"/card-vault\"},{\"path\":\"/leaderboard\"},{\"path\":\"/markets\"},{\"path\":\"/points\"},{\"path\":\"/portfolio\"},{\"path\":\"/pre-deposits\"},{\"path\":\"/r\"},{\"path\":\"/rewards\"},{\"path\":\"/rewards/first-trade-on-us\"},{\"path\":\"/vaults\"},{\"path\":\"/auth\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.2.6";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import * as Sentry from "@sentry/nextjs";

import { env, isProduction } from "./env";
import { recordAttribution } from "./lib/analytics/attribution";
import { captureOverrideWallet } from "./lib/analytics/override-wallet-capture";
import { hasSensitiveFragment, sanitizeAddressBar } from "./lib/analytics/url-allowlist";
import { isWalletInjectedEvent, scrubBreadcrumbUrls, scrubEvent } from "./sentry-filters";
import { getAnonymousId } from "./utils/anonymous-id";

// Runs before hydration and before RudderStack/gtag load, so neither the router nor any
// analytics SDK sees non-allowlisted params. Order matters: capture, record, then strip.
try {
  captureOverrideWallet(window.location.search, { isProduction });
  recordAttribution(window.location.href, document.referrer, Date.now());
  sanitizeAddressBar({ isProduction });
} catch {
  // Never block boot on URL hygiene.
}

if (env.NEXT_PUBLIC_SENTRY_ENVIRONMENT) {
  Sentry.init({
    dsn: "https://7987e47f18846d1b082a0a3ab7e380f5@o1162451.ingest.us.sentry.io/4509765866946560",

    environment: env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,

    // Identify logged-out traffic from the very first event. Set here rather
    // than in a provider effect so it also covers /auth/*, maintenance mode,
    // prerendered tabs, and anything thrown before React mounts.
    // `DecibelWalletProvider` overrides this with the wallet address on connect.
    initialScope: { user: { id: getAnonymousId() } },

    // Replay integration is loaded lazily below to keep it out of the first-load
    // bundle (~90KB gz). Init-time integrations remain empty.
    integrations: [],

    // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
    tracesSampleRate: 0.05,

    // Enable logs to be sent to Sentry
    enableLogs: true,

    // Define how likely Replay events are sampled.
    // Sentry recommends the following values:
    // 0.01 for 100k+ sessions/day
    // 0.1 for 10k-100k sessions/day
    // 0.25 for <10k sessions/day
    replaysSessionSampleRate: 0.05,

    // Define how likely Replay events are sampled when an error occurs.
    replaysOnErrorSampleRate: 0.75,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,

    // Use tunnel route to avoid CSP issues
    tunnel: "/monitoring",

    ignoreErrors: [
      "Hydration",
      "Hydration Error",
      "React error #418",
      "https://react.dev/errors/418",
      // Third-party wallet/extension/userscript noise — not emitted by our code.
      "Identifier '_' has already been declared",
      "timerIds is not defined",
      // iOS dApp in-app browser probing for window.rainbowkit. See DECIBEL-WEB-VD.
      // Anchored regex so we don't accidentally swallow real errors (e.g.
      // "Provider not found rainbowkit/foo") if RainbowKit is ever adopted.
      /^(Error: )?not found rainbowkit$/i,
      // React Native WebView host-app shim (Trust/Phantom/Coinbase mobile in-app browsers).
      "ReactNativeHistoryShim",
    ],
    denyUrls: [
      // Injected by wallet extensions / userscripts, not served by us.
      /window\.canton\.js/i,
      /userscript\.html/i,
      /inpage\.js/i,
      /pageProvider\.js/i,
      /chrome-extension:/i,
      /moz-extension:/i,
    ],
    beforeSend(event) {
      // Drop wallet-injected setInterval/setTimeout callbacks whose error
      // message is too generic for `ignoreErrors`. See `sentry-filters.ts`.
      if (isWalletInjectedEvent(event)) return null;
      return scrubEvent(event);
    },
    beforeSendTransaction: (event) => scrubEvent(event),
    beforeBreadcrumb: (breadcrumb) => scrubBreadcrumbUrls(breadcrumb),
  });

  // Defer the Replay bundle (~90KB gz) to idle so it doesn't compete with
  // first paint. Session/error sampling then runs against the deferred
  // integration — small pre-idle window is not captured, acceptable tradeoff.
  const loadReplay = () => {
    // The auth callback must retain its token until React consumes it, so keep Replay
    // disabled instead of mutating the fragment before the integration reads the URL.
    if (hasSensitiveFragment(window.location.hash)) return;
    Sentry.lazyLoadIntegration("replayIntegration")
      .then((replayIntegration) =>
        Sentry.addIntegration(
          // Self-hosted compression worker (copied out of the installed SDK by
          // scripts/build-sentry-replay-worker.mjs). Without this, replay builds
          // its worker from a `blob:` URL, which our CSP blocks — see the
          // `worker-src` directive in middleware.ts. If the file ever fails to
          // load, replay falls back to an uncompressed buffer rather than dying.
          replayIntegration({ workerUrl: "/sentry-replay-worker.js" }),
        ),
      )
      .catch((error: unknown) => {
        // Non-critical: replay stays disabled if the CDN bundle can't load. Log
        // it because the bundle URL is pinned to the installed SDK version —
        // an SDK bump with no published CDN bundle would otherwise silently
        // disable session replay everywhere.
        Sentry.logger.warn("Failed to lazy-load Sentry replay integration", { error });
      });
  };
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(loadReplay, { timeout: 4000 });
  } else {
    // Safari < 18.4 has no requestIdleCallback.
    setTimeout(loadReplay, 2000);
  }
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
